import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Container } from 'react-bootstrap';
import Link from '../../components/Link';
import lpApi from '../../helpers/lpApi';

// ============================================================================
// CCPA/CPRA §1798.135 — "Do Not Sell or Share My Personal Information".
//
// Required public disclosure even though BestHand does NOT sell or share
// personal information for money or cross-context behavioral advertising. The
// page states that fact (matching the Privacy Policy copy) and provides the
// statutory opt-out toggle for logged-in users. The toggle calls setDoNotSell
// on besthand-api through the `/lp-graphql` proxy (UNGATED — general privacy
// right, independent of the Large Projects flag).
// ============================================================================

const Q_SETTINGS = `query { myPrivacySettings { status doNotSellOptOut } }`;
const M_SET = `
  mutation ($optOut: Boolean!) {
    setDoNotSell(optOut: $optOut) { status errorMessage doNotSellOptOut }
  }
`;

const S = {
  page: { maxWidth: 820, margin: '0 auto', padding: '24px 0 56px', fontFamily: "'Inter', -apple-system, sans-serif", color: '#0f1214', lineHeight: 1.65 },
  h1: { margin: '0 0 10px', fontSize: 30, fontWeight: 800 },
  lead: { color: '#374151', fontSize: 16, marginBottom: 22 },
  p: { fontSize: 15.5, color: '#374151', margin: '0 0 16px' },
  card: { border: '1px solid #e7e9ec', borderRadius: 16, padding: '22px 24px', boxShadow: '0 6px 18px rgba(15,18,20,0.03)', marginBottom: 24 },
  toggleRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' },
  toggleLabel: { fontSize: 16, fontWeight: 800 },
  toggleHint: { color: '#6b7280', fontSize: 13.5, marginTop: 4, maxWidth: 520 },
  btn: { background: '#e8232b', color: '#fff', border: 'none', borderRadius: 10, padding: '11px 18px', fontSize: 14.5, fontWeight: 700, cursor: 'pointer' },
  btnGhost: { background: '#fff', color: '#0f1214', border: '1px solid #e7e9ec', borderRadius: 10, padding: '11px 18px', fontSize: 14.5, fontWeight: 700, cursor: 'pointer' },
  btnDisabled: { opacity: 0.55, cursor: 'not-allowed' },
  state: (on) => ({ display: 'inline-block', borderRadius: 999, padding: '4px 12px', fontSize: 12.5, fontWeight: 800, background: on ? '#e7f6ec' : '#eef0f2', color: on ? '#137a37' : '#374151', marginBottom: 12 }),
  notice: (type) => ({ borderRadius: 12, padding: '12px 16px', fontSize: 13.5, marginBottom: 16, border: `1px solid ${type === 'error' ? '#f3c2c4' : '#bfe6cd'}`, background: type === 'error' ? '#fdf2f2' : '#f0faf3', color: '#374151' }),
  link: { color: '#c2151c', fontWeight: 700, textDecoration: 'none' }, // A11Y 1.4.3: #e8232b is 4.46:1 on white; #c2151c is 6.14:1
};

function DoNotSell() {
  const isAuthenticated = useSelector((state) => state?.runtime?.isAuthenticated);
  const [optOut, setOptOut] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    if (!isAuthenticated) { setLoaded(true); return; }
    try {
      const { data } = await lpApi(Q_SETTINGS);
      setOptOut(!!(data && data.myPrivacySettings && data.myPrivacySettings.doNotSellOptOut));
    } catch (e) { /* show default */ }
    setLoaded(true);
  }, [isAuthenticated]);

  useEffect(() => { load(); }, [load]);

  const submit = async (next) => {
    setBusy(true);
    setMsg(null);
    try {
      const { data, errors } = await lpApi(M_SET, { optOut: next });
      if (errors && errors.length) {
        setMsg({ type: 'error', text: errors[0].message });
      } else if (data && data.setDoNotSell && data.setDoNotSell.status === 200) {
        setOptOut(!!data.setDoNotSell.doNotSellOptOut);
        setMsg({ type: 'success', text: next ? 'Your opt-out preference has been recorded.' : 'Your opt-out preference has been turned off.' });
      } else {
        setMsg({ type: 'error', text: (data && data.setDoNotSell && data.setDoNotSell.errorMessage) || 'Could not save your preference.' });
      }
    } catch (e) {
      setMsg({ type: 'error', text: e.message });
    }
    setBusy(false);
  };

  return (
    <div className={'subPageContainer'}>
      <Container>
        <div className={'subPageWrapper'} style={S.page}>
          <h1 style={S.h1}>Do Not Sell or Share My Personal Information</h1>
          <p style={S.lead}>
            BestHand does <strong>not</strong> sell your personal information for money, and we do{' '}
            <strong>not</strong> share it for cross-context behavioral advertising.
          </p>
          <p style={S.p}>
            We share personal information only as described in our{' '}
            <Link to="/privacy-policy" style={S.link}>Privacy Policy</Link> — for example, between
            Customers and Pros to perform a job, with service providers acting on our behalf
            (payments, hosting, communications), and where required by law. None of these are a
            “sale” or “share” for advertising purposes under the CCPA/CPRA.
          </p>
          <p style={S.p}>
            Because we do not sell or share your information, there is nothing to opt out of. We
            still provide the control below so you can record your preference, and we will honor it.
          </p>

          <div style={S.card}>
            {!isAuthenticated ? (
              <div>
                <p style={S.toggleLabel}>Record your opt-out</p>
                <p style={S.toggleHint}>
                  Please <Link to="/login" style={S.link}>sign in</Link> to record your “Do Not Sell
                  or Share” preference on your account. You can also email{' '}
                  <a href="mailto:privacy@besthandapp.com" style={S.link}>privacy@besthandapp.com</a>.
                </p>
              </div>
            ) : !loaded ? (
              <p style={S.toggleHint}>Loading your preference…</p>
            ) : (
              <div>
                {msg && <div style={S.notice(msg.type)}>{msg.text}</div>}
                <div style={S.state(optOut)}>{optOut ? 'Opted out' : 'Not opted out'}</div>
                <div style={S.toggleRow}>
                  <div>
                    <div style={S.toggleLabel}>Opt out of any sale or sharing of my personal information</div>
                    <div style={S.toggleHint}>
                      Recording this preference confirms your choice on your account. BestHand does
                      not sell or share your information either way.
                    </div>
                  </div>
                  <button
                    type="button"
                    style={{ ...(optOut ? S.btnGhost : S.btn), ...(busy ? S.btnDisabled : {}) }}
                    disabled={busy}
                    onClick={() => submit(!optOut)}
                  >
                    {busy ? 'Saving…' : optOut ? 'Turn off opt-out' : 'Opt out'}
                  </button>
                </div>
              </div>
            )}
          </div>

          <p style={S.p}>
            To exercise your other privacy rights (access, correction, deletion), visit{' '}
            <Link to="/data-rights" style={S.link}>Your privacy &amp; data rights</Link> or contact us
            at <a href="mailto:privacy@besthandapp.com" style={S.link}>privacy@besthandapp.com</a>.
          </p>
        </div>
      </Container>
    </div>
  );
}

export default DoNotSell;
